import { apply, bump, celebrate, counter, onceToday, toast } from '../core/effects';
import { dayNumber, logShadow, logSpell, logTalk, logWord } from '../content/passport';
import { storiesTab } from './stories';
import { S } from '../core/game';
import { m } from '../i18n';
import { sayEnglish } from '../audio/speech';
import { sfx } from '../audio/sfx';
import { CATS, SCENARIOS, SCENARIO_BY_AREA, WORDS, type ScenarioId, type VocabCat, type Word } from '../content/vocab';
import { h } from './dom';
import { openPanel, refreshPanel } from './panel';
import { listenTab, nextListen } from './lifeListen';
import { recognizer } from './lifeMic';

/** English corner: learn by topic, spaced-repetition review, spelling, and role-play talks. */
type Tab = 'learn' | 'review' | 'spell' | 'talk' | 'read' | 'listen';

const closeHooks: (() => void)[] = [];
export function onEnglishClose(fn: () => void): void {
  closeHooks.push(fn);
}

/** Where you are, so the matching role-play is offered first. */
let areaHint: () => string = () => '';
export function setEnglishArea(fn: () => string): void {
  areaHint = fn;
}

const ui = {
  tab: 'learn' as Tab,
  cat: 'school' as VocabCat,
  review: { word: null as Word | null, shown: false, choice: false, options: [] as string[] },
  spell: { word: null as Word | null, tiles: [] as { ch: string; used: boolean }[], typed: [] as number[], result: '' as '' | 'ok' | 'bad' },
  talk: { id: null as ScenarioId | null, turn: 0, feedback: '' as '' | 'ok' | 'bad', misses: 0 },
};

/** Leitner boxes: days until the next review for each box. */
const BOX_DAYS = [0, 1, 2, 4, 7, 14];

function goal(id: ScenarioId): string {
  const t = m();
  return (t.english2.scenarios as Record<string, string>)[id] ?? (t.places.scenarios as Record<string, string>)[id] ?? id;
}

function meaning(w: string): string {
  const t = m();
  return (t.english2.meanings as Record<string, string>)[w] ?? (t.english.meanings as Record<string, string>)[w] ?? w;
}

const dayNo = dayNumber;

export function addToDeck(w: string): void {
  const s = S();
  if (!s.vocab[w]) s.vocab[w] = { box: 0, due: dayNo() };
}

function dueWords(): Word[] {
  const s = S();
  const today = dayNo();
  return WORDS.filter((w) => s.vocab[w.w] && s.vocab[w.w].due <= today && !s.daily[`rev.${w.w}`]);
}

function grade(w: string, knew: boolean): void {
  const s = S();
  const card = s.vocab[w] ?? { box: 0, due: 0 };
  card.box = knew ? Math.min(BOX_DAYS.length - 1, card.box + 1) : 0;
  card.due = dayNo() + (knew ? BOX_DAYS[card.box] : 0);
  s.vocab[w] = card;
  // A missed word comes back later in the same session; a known one waits for its box.
  if (knew) s.daily[`rev.${w}`] = true;
  if (knew && counter('revStars') < 15) {
    bump('revStars', 1);
    apply({ stars: 1, study: 1 }, { float: false });
  }
  sfx(knew ? 'ok' : 'pop');
}

function shuffle<T>(a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function nextReview(): void {
  const due = shuffle(dueWords());
  const word = due[0] ?? null;
  ui.review = { word, shown: false, choice: Math.random() < 0.5, options: [] };
  if (word) {
    const others = shuffle(WORDS.filter((x) => x.w !== word.w)).slice(0, 3).map((x) => x.w);
    ui.review.options = shuffle([word.w, ...others]);
  }
}

function nextSpell(): void {
  const s = S();
  const pool = WORDS.filter((w) => s.vocab[w.w] && w.w.length <= 10);
  const list = pool.length ? pool : WORDS.filter((w) => w.cat === ui.cat && w.w.length <= 10);
  const word = list[Math.floor(Math.random() * list.length)];
  const decoys = 'aeioustrnl'.split('').filter((ch) => !word.w.includes(ch)).slice(0, 2);
  ui.spell = { word, tiles: shuffle([...word.w.split(''), ...decoys]).map((ch) => ({ ch, used: false })), typed: [], result: '' };
  sayEnglish(word.w, 0.8);
}

/** Vocabulary topic that matches each place. */
const CAT_BY_AREA: Record<string, VocabCat> = {
  library: 'library', fastFood: 'food', cafeteria: 'food', cafe: 'food', restaurant: 'food', store: 'shopping', mall: 'shopping', giftShop: 'shopping', dept: 'shopping',
  gym: 'sports', pool: 'sports', tennis: 'sports', volley: 'sports', field: 'sports', sportsCenter: 'sports', nurse: 'health', restroom: 'health',
  home: 'home', neighborhood: 'home', park: 'nature', yard: 'nature', stable: 'nature', classroom: 'school', counsel: 'feelings', corridor: 'school',
};

export function openEnglish(): void {
  const cat = CAT_BY_AREA[areaHint()];
  if (cat) ui.cat = cat;
  openPanel('english', render, () => closeHooks.forEach((f) => f()));
}

// --- Shadowing with the microphone (Web Speech API, where the browser has it) ---------------
const micState = new Map<string, 'listening' | 'ok' | 'bad'>();
const heardText = new Map<string, string>();

function shadow(w: Word): void {
  const rec = recognizer();
  if (!rec) return toast(m().english.noMic);
  rec.lang = 'en-US';
  rec.interimResults = false;
  rec.maxAlternatives = 5;
  let done = false;
  micState.set(w.w, 'listening');
  refreshPanel('english');
  rec.onresult = (e) => {
    done = true;
    const alts = Array.from(e.results[0] ?? []).map((a) => a.transcript.trim().toLowerCase());
    const target = w.w.replace(/[^a-z]/g, '');
    const ok = alts.some((a) => a.replace(/[^a-z]/g, '') === target || a.split(/\s+/).includes(w.w));
    heardText.set(w.w, alts[0] ?? '');
    micState.set(w.w, ok ? 'ok' : 'bad');
    if (ok) {
      addToDeck(w.w);
      logShadow(w.w);
      if (onceToday(`shadow.${w.w}`)) {
        bump('wordsToday');
        celebrate();
        apply({ stars: 2, study: 2 });
      } else sfx('ok');
    } else sfx('bad');
    refreshPanel('english');
  };
  rec.onerror = () => {
    done = true;
    micState.delete(w.w);
    toast(m().english.noMic);
    refreshPanel('english');
  };
  rec.onend = () => {
    if (!done) {
      micState.delete(w.w);
      refreshPanel('english');
    }
  };
  try {
    rec.start();
  } catch {
    micState.delete(w.w);
    toast(m().english.noMic);
  }
}

/** Jump straight to the role-play that fits where you are. */
export function openEnglishHere(): void {
  const here = SCENARIO_BY_AREA[areaHint()];
  ui.tab = 'talk';
  ui.talk = { id: null, turn: 0, feedback: '', misses: 0 };
  if (here) {
    ui.talk.id = here;
    sayEnglish(SCENARIOS[here].turns[0].npc, 0.9);
  }
  openEnglish();
}

function render() {
  const t = m().english2;
  const keys = [...(Object.keys(t.tabs) as Tab[])];
  keys.splice(Math.min(3, keys.length), 0, 'listen');
  const label = (k: Tab) => (k === 'listen' ? m().life.listen.tab : (t.tabs as Record<string, string>)[k]);
  const tabs = keys.map((k) =>
    h(
      'button',
      {
        class: `chip${ui.tab === k ? ' on' : ''}`,
        onclick: () => {
          sfx('click');
          ui.tab = k;
          if (k === 'review') nextReview();
          if (k === 'spell') nextSpell();
          if (k === 'talk') ui.talk = { id: null, turn: 0, feedback: '', misses: 0 };
          if (k === 'listen') nextListen();
          refreshPanel('english');
        },
      },
      k === 'review' ? `${label(k)} (${dueWords().length})` : label(k),
    ),
  );
  const body = ui.tab === 'learn' ? learn() : ui.tab === 'review' ? review() : ui.tab === 'spell' ? spell() : ui.tab === 'read' ? storiesTab() : ui.tab === 'listen' ? listenTab() : talk();
  const mastered = Object.values(S().vocab).filter((v) => v.box >= 3).length;
  return {
    title: t.title,
    className: 'english',
    body: [h('div', { class: 'chips tabs' }, tabs), h('p', { class: 'hint' }, t.mastered(mastered, WORDS.length)), body],
  };
}

function learn() {
  const t = m().english2;
  const s = S();
  const hasMic = recognizer() !== null;
  return [
    h('p', { class: 'hint' }, t.learnHint),
    h(
      'div',
      { class: 'chips' },
      CATS.map((c) => h('button', { class: `chip small${ui.cat === c ? ' on' : ''}`, onclick: () => ((ui.cat = c), sfx('click'), refreshPanel('english')) }, t.categories[c])),
    ),
    WORDS.filter((w) => w.cat === ui.cat).map((w) =>
      h(
        'div',
        { class: `card vocab${s.vocab[w.w] ? ' known' : ''}` },
        h('div', { class: 'row' }, h('button', { class: 'word', onclick: () => sayEnglish(w.w) }, w.w), h('span', { class: 'meaning' }, meaning(w.w))),
        h('button', { class: 'example', onclick: () => sayEnglish(w.ex, 0.85) }, `🔊 ${w.ex}`),
        micState.get(w.w) === 'listening'
          ? h('p', { class: 'hint ok' }, m().english.listening)
          : micState.get(w.w)
            ? h('p', { class: `hint ${micState.get(w.w)}` }, `${m().english.heard(heardText.get(w.w) || '…')} ${micState.get(w.w) === 'ok' ? '✅' : '🔁'}`)
            : null,
        hasMic
          ? h('button', { class: 'btn small blue wide', onclick: () => (sayEnglish(w.w, 0.75), window.setTimeout(() => shadow(w), 900)) }, m().english.tryMic)
          : null,
        h(
          'button',
          {
            class: 'btn small green wide',
            onclick: () => {
              sayEnglish(w.w, 0.75);
              addToDeck(w.w);
              if (onceToday(`learn.${w.w}`)) {
                bump('wordsToday');
                logWord();
                apply({ stars: 1, study: 1 }, { float: false });
              }
              toast(`📒 ${w.w} — ${meaning(w.w)}`);
              refreshPanel('english');
            },
          },
          s.vocab[w.w] ? `✅ ${m().english.practice}` : m().english.practice,
        ),
      ),
    ),
  ];
}

function review() {
  const t = m().english2;
  const r = ui.review;
  if (!r.word) nextReview();
  if (!ui.review.word) return [h('p', { class: 'hint big' }, t.reviewNone)];
  const w = ui.review.word;
  const done = () => {
    nextReview();
    refreshPanel('english');
  };
  if (ui.review.choice) {
    return [
      h('p', { class: 'hint' }, t.reviewDue(dueWords().length)),
      h('div', { class: 'flash' }, h('small', null, t.choose), h('b', null, meaning(w.w))),
      h(
        'div',
        { class: 'grid2' },
        ui.review.options.map((o) =>
          h(
            'button',
            {
              class: 'btn',
              onclick: () => {
                const ok = o === w.w;
                sayEnglish(w.w);
                grade(w.w, ok);
                toast(ok ? t.talkGood : `${w.w} = ${meaning(w.w)}`);
                done();
              },
            },
            o,
          ),
        ),
      ),
    ];
  }
  return [
    h('p', { class: 'hint' }, `${t.reviewHint} · ${t.reviewDue(dueWords().length)}`),
    h('div', { class: 'flash' }, h('b', null, meaning(w.w)), ui.review.shown ? h('div', { class: 'answer' }, w.w, h('small', null, w.ex)) : null),
    ui.review.shown
      ? h(
          'div',
          { class: 'grid2' },
          h('button', { class: 'btn green', onclick: () => (grade(w.w, true), done()) }, t.knew),
          h('button', { class: 'btn ghost', onclick: () => (grade(w.w, false), done()) }, t.again),
        )
      : h('button', { class: 'btn gold wide', onclick: () => ((ui.review.shown = true), sayEnglish(w.w), refreshPanel('english')) }, t.show),
  ];
}

function spell() {
  const t = m().english2;
  if (!ui.spell.word) nextSpell();
  const sp = ui.spell;
  const w = sp.word!;
  const typed = sp.typed.map((i) => sp.tiles[i].ch).join('');
  const check = (full: string) => {
    if (full.length < w.w.length) return;
    if (full === w.w) {
      sp.result = 'ok';
      logSpell(w.w);
      if (onceToday(`spell.${w.w}`)) {
        celebrate();
        apply({ stars: 2, study: 2 });
      } else sfx('ok');
      addToDeck(w.w);
    } else {
      sp.result = 'bad';
      sfx('bad');
    }
  };
  return [
    h('p', { class: 'hint' }, t.spellHint),
    h(
      'div',
      { class: 'flash' },
      // In English the meaning is often the word itself: show the example with a gap instead.
      h('b', null, meaning(w.w).toLowerCase().includes(w.w) ? w.ex.replace(new RegExp(`\\b${w.w}\\b`, 'ig'), '___') : meaning(w.w)),
      h('button', { class: 'btn small blue', onclick: () => sayEnglish(w.w, 0.7) }, t.hear),
    ),
    h('div', { class: `spell-slots ${sp.result}` }, w.w.split('').map((_, i) => h('span', { class: 'slot' }, typed[i] ?? ''))),
    h(
      'div',
      { class: 'spell-tiles' },
      sp.tiles.map((tile, i) =>
        h(
          'button',
          {
            class: 'tile',
            disabled: tile.used || sp.result === 'ok',
            onclick: () => {
              tile.used = true;
              sp.typed.push(i);
              sfx('click');
              check(sp.typed.map((k) => sp.tiles[k].ch).join(''));
              refreshPanel('english');
            },
          },
          tile.ch,
        ),
      ),
    ),
    h(
      'div',
      { class: 'grid2' },
      h(
        'button',
        {
          class: 'btn ghost',
          onclick: () => {
            const i = sp.typed.pop();
            if (i !== undefined) sp.tiles[i].used = false;
            sp.result = '';
            refreshPanel('english');
          },
        },
        t.undo,
      ),
      h('button', { class: 'btn gold', onclick: () => (nextSpell(), refreshPanel('english')) }, t.next),
    ),
    sp.result ? h('p', { class: `hint big ${sp.result}` }, sp.result === 'ok' ? `✅ ${t.spellOk}` : `❌ ${t.spellBad}`) : null,
  ];
}

function talk() {
  const t = m().english2;
  const tk = ui.talk;
  if (!tk.id) {
    const here = SCENARIO_BY_AREA[areaHint()];
    const ids = Object.keys(SCENARIOS) as ScenarioId[];
    ids.sort((a, b) => Number(b === here) - Number(a === here));
    return [
      h('p', { class: 'hint' }, `${t.talkHint} ${t.talkPick}`),
      ids.map((id) =>
        h(
          'button',
          {
            class: `scenario${id === here ? ' here' : ''}`,
            onclick: () => {
              ui.talk = { id, turn: 0, feedback: '', misses: 0 };
              sayEnglish(SCENARIOS[id].turns[0].npc, 0.9);
              refreshPanel('english');
            },
          },
          h('span', { class: 'ico' }, SCENARIOS[id].icon),
          h('span', null, goal(id)),
          id === here ? h('small', null, t.hereFirst) : null,
        ),
      ),
    ];
  }
  const sc = SCENARIOS[tk.id];
  const turn = sc.turns[tk.turn];
  if (!turn) {
    return [
      h('div', { class: 'flash' }, h('b', null, `🎉 ${t.talkDone(goal(tk.id))}`)),
      h('button', { class: 'btn gold wide', onclick: () => ((ui.talk = { id: null, turn: 0, feedback: '', misses: 0 }), refreshPanel('english')) }, t.next),
    ];
  }
  // Shuffle the options once per turn so the right answer isn't always first.
  const opts = [...turn.options].sort((a, b) => ((a.t.length * 7 + tk.turn) % 5) - ((b.t.length * 7 + tk.turn) % 5));
  return [
    h('p', { class: 'hint' }, goal(tk.id)),
    h('div', { class: 'talk-line' }, h('span', { class: 'ico' }, sc.icon), h('button', { class: 'say', onclick: () => sayEnglish(turn.npc, 0.9) }, `🔊 ${turn.npc}`)),
    h(
      'div',
      { class: 'talk-options' },
      opts.map((o) =>
        h(
          'button',
          {
            class: 'btn talk-opt',
            onclick: () => {
              sayEnglish(o.t, 0.9);
              if (o.ok) {
                tk.turn++;
                tk.feedback = 'ok';
                sfx('ok');
                const nxt = sc.turns[tk.turn];
                if (nxt) window.setTimeout(() => sayEnglish(nxt.npc, 0.9), 1300);
                else logTalk(tk.id!, tk.misses);
                if (!nxt && onceToday(`talk.${tk.id}`)) {
                  celebrate(true);
                  apply({ stars: 5, study: 4 });
                }
              } else {
                tk.feedback = 'bad';
                tk.misses++;
                sfx('bad');
              }
              refreshPanel('english');
            },
          },
          o.t,
        ),
      ),
    ),
    tk.feedback ? h('p', { class: `hint big ${tk.feedback}` }, tk.feedback === 'ok' ? `✅ ${t.talkGood}` : `🤔 ${t.talkBad}`) : null,
  ];
}
